/**
 * CMS SDK - useContent Hook
 * 内容管理 Hook
 */

import { useState, useCallback } from 'react';
import { useCMS } from '../context/CMSContext';
import { ContentStatus } from '../types';

/**
 * 内容管理 Hook
 * @param {string} contentId - 可选的内容 ID
 * @returns {Object} 内容操作对象
 */
export function useContent(contentId = null) {
  const { api, currentContent, setCurrentContent } = useCMS();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(null);

  /**
   * 加载内容
   */
  const load = useCallback(async (id = contentId) => {
    if (!id) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getContent(id);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 保存内容（创建或更新）
   */
  const save = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      let result;
      if (data.id) {
        result = await api.updateContent(data.id, data);
      } else {
        result = await api.createContent(data);
      }
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 保存草稿
   */
  const saveDraft = useCallback(async (data) => {
    return save({ ...data, status: ContentStatus.DRAFT });
  }, [save]);

  /**
   * 删除内容
   */
  const remove = useCallback(async (id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      await api.deleteContent(id);
      setContent(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 发布内容
   */
  const publish = useCallback(async (id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.publishContent(id);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 取消发布
   */
  const unpublish = useCallback(async (id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.unpublishContent(id);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 定时发布
   */
  const schedule = useCallback(async (scheduledAt, id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.scheduleContent(id, scheduledAt);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 归档内容
   */
  const archive = useCallback(async (id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.archiveContent(id);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  /**
   * 提交审核
   */
  const submitReview = useCallback(async (id = contentId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.submitReview(id);
      setContent(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  return {
    content: content || currentContent,
    loading,
    error,
    load,
    save,
    saveDraft,
    remove,
    publish,
    unpublish,
    schedule,
    archive,
    submitReview,
  };
}

export default useContent;
